import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCcw } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import SeedDataButton from '@/components/admin/SeedDataButton';
import { invokeFunction } from '@/lib/utils/invokeFunction';
import { useAuth } from '@/context/AuthContext';
import { resetUserCredits } from '@/lib/actions/adminActions';
import { useToast } from '@/hooks/use-toast';

interface CleanupResponse {
  success?: boolean;
  message?: string;
  affected_count: number;
  timestamp?: string;
}

interface PermanentDeletionResponse {
  success?: boolean;
  message?: string;
  deleted_count: number;
  skipped_count: number;
  timestamp?: string;
}

const AdminTools = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isResettingCredits, setIsResettingCredits] = useState(false);

  const { data: categories, isLoading: categoriesLoading, refetch: refetchCategories } = useQuery({
    queryKey: ['categoriesWithCounts'],
    queryFn: async () => {
      try {
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*');
          
        if (categoriesError) throw categoriesError;
        
        // For each category, count the listings
        const categoriesWithCountsPromises = categoriesData.map(async (category) => {
          const { count, error: countError } = await supabase
            .from('listings')
            .select('*', { count: 'exact', head: true })
            .eq('category_id', category.id);
            
          if (countError) console.error('Error counting listings:', countError);
          
          return {
            ...category,
            count: count || 0
          };
        });
        
        return Promise.all(categoriesWithCountsPromises);
      } catch (error) {
        console.error('Error fetching categories with counts:', error);
        return [];
      }
    },
  });

  const handleRunCleanup = async () => {
    try {
      const { data, error } = await invokeFunction<CleanupResponse>('cleanup-old-listings');
      if (error) throw error;
      alert(`Cleanup completed: ${data?.affected_count || 0} listings processed`);
    } catch (err) {
      console.error('Error running cleanup:', err);
      alert('Error running cleanup function');
    }
  };

  const handleRunPermanentDeletion = async () => {
    try {
      const { data, error } = await invokeFunction<PermanentDeletionResponse>('permanent-delete-old-listings');
      if (error) throw error;
      alert(`Permanent deletion completed: ${data?.deleted_count || 0} listings deleted, ${data?.skipped_count || 0} skipped`);
    } catch (err) {
      console.error('Error running permanent deletion:', err);
      alert('Error running permanent deletion function');
    }
  };

  const handleResetOwnCredits = async () => {
    if (!profile?.id) {
      toast({ title: "Error", description: "User profile not found.", variant: "destructive" });
      return;
    }
    setIsResettingCredits(true);
    try {
      const result = await resetUserCredits(profile.id);
      if (result.success) {
        toast({ title: "Success", description: result.message || "Your credits have been reset." });
        // Invalidate queries to refresh credit display
        queryClient.invalidateQueries({ queryKey: ['userCredits', profile.id] });
        queryClient.invalidateQueries({ queryKey: ['userCredits'] }); // For general credit hooks
        queryClient.invalidateQueries({ queryKey: ['useCreditsReset', profile.id] }); // For useCreditsReset hook
      } else {
        toast({ title: "Error", description: result.error || "Failed to reset credits.", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "An unexpected error occurred.", variant: "destructive" });
    } finally {
      setIsResettingCredits(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Admin Tools</CardTitle>
        <CardDescription>
          Tools for managing test data in the application
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h3 className="text-lg font-medium mb-2">Generate Sample Data</h3>
          <p className="text-sm text-gray-500 mb-4">
            This will create sample listings for all categories to help you test the application.
          </p>
          <SeedDataButton />
        </div>
        
        <div className="pt-4 border-t">
          <h3 className="text-lg font-medium mb-2">Maintenance Tools</h3>
          <div className="flex flex-col space-y-4">
            <div>
              <p className="text-sm text-gray-500 mb-2">
                Archive listings older than 90 days. Daily automatic process.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={handleRunCleanup}
              >
                Run Cleanup Process
              </Button>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-2">
                Permanently delete listings marked for deletion older than 80 days. Daily automatic process.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={handleRunPermanentDeletion}
              >
                Run Permanent Deletion
              </Button>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t">
          <h3 className="text-lg font-medium mb-2">Credit Management</h3>
          <div>
            <p className="text-sm text-gray-500 mb-2">
              Manually reset your own free monthly credits. This will set your free listings used to 0 and update your reset date.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full flex items-center"
              onClick={handleResetOwnCredits}
              disabled={isResettingCredits}
            >
              {isResettingCredits ? (
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
              ) : (
                <RefreshCcw className="h-4 w-4 mr-2" />
              )}
              Reset My Admin Credits
            </Button>
          </div>
        </div>
        
        <div className="pt-4 border-t">
          <h3 className="text-lg font-medium mb-2">Category Statistics</h3>
          {categoriesLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="animate-spin h-6 w-6 text-gray-400" />
            </div>
          ) : (
            <div className="space-y-2">
              {categories?.map(category => (
                <div key={category.id} className="flex justify-between items-center text-sm">
                  <span>{category.name}</span>
                  <span className="bg-gray-100 py-1 px-2 rounded-full text-xs">
                    {category.count} listings
                  </span>
                </div>
              ))}
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-2"
                onClick={() => refetchCategories()}
              >
                Refresh Count
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminTools;
